import React, { useState, useEffect, useRef } from 'react';
import './ShipmentList.css';
import { API_BASE_URL } from '../config/api';
import SingleShipmentView from './SingleShipmentView';

interface Shipment {
  id: number;
  name: string;
  status: string;
  created_at: string;
  items: ShipmentItem[];
}

interface ShipmentItem {
  id: number;
  name: string;
  required_quantity: number;
  actual_quantity?: number;
  is_completed: boolean;
  is_checked?: boolean;
  notes?: string;
  updated_at: string;
  batches: ShipmentBatch[];
}

interface ShipmentBatch {
  id: number;
  zoho_batch_id: string;
  zoho_batch_number: string;
  quantity_selected: number;
  storage_locations?: ShipmentBatchStorage[];
}

interface ShipmentBatchStorage {
  id: number;
  zoho_storage_id: string;
  storage_name: string;
  warehouse_id: string;
  warehouse_name: string;
  quantity: number;
  total_available_quantity?: number;
}

const ShipmentList: React.FC = () => {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedShipmentId, setExpandedShipmentId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'completed'>('pending');
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    actual_quantity: '',
    is_completed: false,
    is_checked: true,
    notes: ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [viewingNotesItem, setViewingNotesItem] = useState<number | null>(null);
  const [editingNotesItem, setEditingNotesItem] = useState<number | null>(null);
  const [notesEditForm, setNotesEditForm] = useState('');
  const [processingShipment, setProcessingShipment] = useState<number | null>(null);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [exportPopupOpen, setExportPopupOpen] = useState<number | null>(null);
  const [exportPopupPosition, setExportPopupPosition] = useState<{x: number, y: number}>({x: 0, y: 0});
  const scrollPositionRef = useRef<number>(0);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [shiftXCount, setShiftXCount] = useState(0);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'all' | 'custom'>('today');
  const [searchText, setSearchText] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  
  const getDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let startDate: string;
    let endDate: string;
    
    
    if (dateFilter === 'all') {
      return { startDate: '', endDate: '' };
    }
    
    
    if (dateFilter === 'custom' && (customStartDate || customEndDate)) {
      startDate = customStartDate || '';
      endDate = customEndDate || today.toISOString().split('T')[0];
    } else {
      
      endDate = today.toISOString().split('T')[0];
      
      switch (dateFilter) {
        case 'today':
          startDate = today.toISOString().split('T')[0];
          break;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          startDate = weekAgo.toISOString().split('T')[0];
          break;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          startDate = monthAgo.toISOString().split('T')[0];
          break;
        default:
          startDate = today.toISOString().split('T')[0];
      }
    }
    
    return { startDate, endDate };
  };

  
  const refreshShipments = async () => {
    try {
      let url = `${API_BASE_URL}/v1/shipments`;
      
      
      if (activeTab === 'completed') {
        const { startDate, endDate } = getDateRange();
        url += `?status=completed`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;
      } else {
        
        url += `?excludeStatus=completed`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setShipments(data);
      }
    } catch (err) {
      console.error('Failed to refresh shipments:', err);
    }
  };

  
  const getFilteredShipments = (): Shipment[] => {
    if (!searchText.trim()) {
      return shipments;
    }
    
    const searchLower = searchText.toLowerCase();
    return shipments.filter(shipment => 
      shipment.name?.toLowerCase().includes(searchLower)
    );
  };

  useEffect(() => {
    const fetchShipments = async () => {
      setLoading(true);
      try {
        let url = `${API_BASE_URL}/v1/shipments`;
        
        
        if (activeTab === 'completed') {
          const { startDate, endDate } = getDateRange();
          url += `?status=completed`;
          if (startDate) url += `&startDate=${startDate}`;
          if (endDate) url += `&endDate=${endDate}`;
        } else {
          
          url += `?excludeStatus=completed`;
        }
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch shipments');
        }
        const data = await response.json();
        setShipments(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, [activeTab, dateFilter, customStartDate, customEndDate]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey && event.key.toLowerCase() === 'x') {
        setShiftXCount(prev => {
          const newCount = prev + 1;
          console.log(`SHIFT+X pressed: ${newCount}/15`);
          
          
          if (resetTimerRef.current) {
            clearTimeout(resetTimerRef.current);
          }
          
          
          resetTimerRef.current = setTimeout(() => {
            setShiftXCount(0);
            console.log('SHIFT+X count reset due to inactivity');
          }, 3000);
          
          if (newCount >= 15) {
            setDeleteEnabled(true);
            console.log('Delete mode enabled for 30 seconds');
            setTimeout(() => {
              setDeleteEnabled(false);
              setShiftXCount(0);
              console.log('Delete mode disabled');
            }, 30000); 
            return 0;
          }
          return newCount;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  

  const deleteShipment = async (shipmentId: number) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете тази пратка?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete shipment');
      }
      
      
      setShipments(shipments.filter(shipment => shipment.id !== shipmentId));
    } catch (err: any) {
      setError(`Грешка при изтриване: ${err.message}`);
    }
  };

  const toggleShipment = (id: number) => {
    setExpandedShipmentId(expandedShipmentId === id ? null : id);
  };

  const viewShipmentDetails = (shipment: Shipment) => {
    
    scrollPositionRef.current = window.scrollY;
    setSelectedShipment(shipment);
  };

  const handleBackToList = () => {
    setSelectedShipment(null);
    
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 0);
  };

  const handleShipmentUpdateInSingleView = (updatedShipment: Shipment) => {
    
    setShipments(shipments.map(s => s.id === updatedShipment.id ? updatedShipment : s));
    
    setSelectedShipment(updatedShipment);
  };

  const startEditItem = (item: ShipmentItem) => {
    setEditingItem(item.id);
    setEditForm({
      actual_quantity: item.actual_quantity?.toString() || '',
      is_completed: item.is_completed,
      is_checked: item.is_checked ?? true,
      notes: item.notes || ''
    });
  };

  const cancelEditItem = () => {
    setEditingItem(null);
    setEditForm({
      actual_quantity: '',
      is_completed: false,
      is_checked: true,
      notes: ''
    });
  };

  const saveEditItem = async (shipmentId: number, itemId: number) => {
    try {
              const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actual_quantity: editForm.actual_quantity ? parseInt(editForm.actual_quantity) : null,
          is_completed: editForm.is_completed,
          is_checked: editForm.is_checked,
          notes: editForm.notes || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update item');
      }

      
      await refreshShipments();
      
      setEditingItem(null);
    } catch (err: any) {
      setError(`Грешка при обновяване: ${err.message}`);
    }
  };

  const viewItemNotes = async (shipmentId: number, itemId: number, currentlyViewing: boolean) => {
    if (!currentlyViewing) {
      
      try {
        await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}/view-notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        
        await refreshShipments();
      } catch (err) {
        console.error('Failed to auto-check item:', err);
      }
    }
    
    setViewingNotesItem(currentlyViewing ? null : itemId);
  };

  const startEditingNotes = (item: ShipmentItem) => {
    setEditingNotesItem(item.id);
    setNotesEditForm(item.notes || '');
    setViewingNotesItem(null); 
  };

  const cancelEditingNotes = () => {
    setEditingNotesItem(null);
    setNotesEditForm('');
  };

  const saveNotesEdit = async (shipmentId: number, itemId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: notesEditForm || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update notes');
      }

      
      await refreshShipments();

      setEditingNotesItem(null);
      setNotesEditForm('');
    } catch (err: any) {
      setError(`Грешка при запазване на бележки: ${err.message}`);
    }
  };

  const removeItemFromShipment = async (shipmentId: number, itemId: number) => {
    if (!window.confirm('Сигурни ли сте, че искате да премахнете този артикул от пратката?')) {
      return;
    }

    try {
      console.log(`× Frontend: Attempting to delete item ${itemId} from shipment ${shipmentId}`);
      
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}`, {
        method: 'DELETE'
      });

      console.log(`× Frontend: Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('× Frontend: Error response:', errorData);
        throw new Error(errorData.message || 'Failed to remove item');
      }

      const responseData = await response.json();
      console.log('× Frontend: Success response:', responseData);

      
      await refreshShipments();
      
      console.log('✅ Frontend: Item removed successfully and data refreshed');
    } catch (err: any) {
      console.error('❌ Frontend: Error removing item:', err);
      setError(`Грешка при премахване: ${err.message}`);
    }
  };

  const handleShipmentStatusChange = async (shipmentId: number) => {
    
    await completeShipment(shipmentId);
  };

  const completeShipment = async (shipmentId: number) => {
    setProcessingShipment(shipmentId);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' })
      });

      if (!response.ok) {
        throw new Error('Failed to complete shipment');
      }

      
      const shipmentToComplete = shipments.find(s => s.id === shipmentId);
      const shipmentName = shipmentToComplete?.name || `Пратка #${shipmentId}`;

      
      await refreshShipments();
      
      
      console.log('🎯 Auto-downloading Zoho export for completed shipment:', shipmentId);
      await exportForZoho(shipmentId, shipmentName);
      
    } catch (err: any) {
      setError(`Грешка при завършване на пратката: ${err.message}`);
    } finally {
      setProcessingShipment(null);
    }
  };

  const uncompleteShipment = async (shipmentId: number) => {
    setProcessingShipment(shipmentId);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'pending' }) 
      });

      if (!response.ok) {
        throw new Error('Failed to uncomplete shipment');
      }

      
      await refreshShipments();
    } catch (err: any) {
      setError(`Грешка при отмяна на завършването: ${err.message}`);
    } finally {
      setProcessingShipment(null);
    }
  };

  const checkUncheckedItems = async (shipmentId: number): Promise<{ hasUnchecked: boolean, uncheckedItems: any[] }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/unchecked-items`);
      if (!response.ok) {
        throw new Error('Failed to check unchecked items');
      }
      const data = await response.json();
      return { hasUnchecked: data.has_unchecked, uncheckedItems: data.unchecked_items };
    } catch (err) {
      console.error('Error checking unchecked items:', err);
      return { hasUnchecked: false, uncheckedItems: [] };
    }
  };

  const exportShipment = async (shipmentId: number, shipmentName: string) => {
    
    const { hasUnchecked, uncheckedItems } = await checkUncheckedItems(shipmentId);
    
    if (hasUnchecked) {
      const itemNames = uncheckedItems.map(item => item.item_name).join('\n• ');
      const proceed = window.confirm(
        `⚠️ Предупреждение!\n\nСледните артикули се нуждаят от ръчна проверка (бележките им не са прегледани):\n\n• ${itemNames}\n\nИскате ли да продължите с експорта?`
      );
      
      if (!proceed) {
        return; 
      }
    }
    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/export`);
      
      if (!response.ok) {
        throw new Error('Failed to export shipment');
      }
      
      
      const now = new Date();
      const sofiaDate = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Sofia"}));
      const dateStr = sofiaDate.toISOString().split('T')[0]; 
      const timeStr = sofiaDate.toTimeString().split(' ')[0].replace(/:/g, '-'); 
      const filename = `shipment_${shipmentId}_${dateStr}_${timeStr}.xlsx`;
      
      
      const blob = await response.blob();
      
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      setError(`Грешка при експорт: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const exportForZoho = async (shipmentId: number, shipmentName: string) => {
    
    const { hasUnchecked, uncheckedItems } = await checkUncheckedItems(shipmentId);
    
    if (hasUnchecked) {
      const itemNames = uncheckedItems.map(item => item.item_name).join('\n• ');
      const proceed = window.confirm(
        `⚠️ Предупреждение!\n\nСледните артикули се нуждаят от ръчна проверка (бележките им не са прегледани):\n\n• ${itemNames}\n\nИскате ли да продължите с Zoho експорта?`
      );
      
      if (!proceed) {
        return; 
      }
    }

    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/export/zoho`);
      
      if (!response.ok) {
        throw new Error('Failed to export shipment for Zoho');
      }
      
      
      const now = new Date();
      const sofiaDate = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Sofia"}));
      const dateStr = sofiaDate.toISOString().split('T')[0]; 
      const timeStr = sofiaDate.toTimeString().split(' ')[0].replace(/:/g, '-'); 
      const filename = `shipment_${shipmentId}_zoho_${dateStr}_${timeStr}.xlsx`;
      
      
      const blob = await response.blob();
      
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      setError(`Грешка при Zoho експорт: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const canCompleteShipment = (shipment: Shipment): boolean => {
    return shipment.status !== 'completed' && shipment.items.every(item => item.is_completed);
  };

  const getStatusDisplayText = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'Завършена';
      case 'pending':
        return 'Незавършена';
      case 'in_progress':
        return 'В процес';
      default:
        return status;
    }
  };

  if (loading) return <div>Зареждане на пратки...</div>;
  if (error) return <div className="error-message">Грешка: {error}</div>;

  
  if (selectedShipment) {
    return (
      <SingleShipmentView
        shipment={selectedShipment}
        onBack={handleBackToList}
        onShipmentUpdate={handleShipmentUpdateInSingleView}
      />
    );
  }

  return (
    <div className="shipment-list-container">
      <h3>Списък на създадените пратки</h3>
      
      {deleteEnabled && (
        <div className="delete-mode-notification">
          🗑️ Режим на изтриване е активен за 30 секунди
        </div>
      )}
      
      {}
      <div className="tabs-container">
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Незавършени ({activeTab === 'pending' ? shipments.length : '...'})
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Завършени ({activeTab === 'completed' ? shipments.length : '...'})
        </button>
      </div>

      {}
      {activeTab === 'completed' && (
      <>
        {}
        <div className="date-filter-container">
          <button 
            className={`date-filter-button ${dateFilter === 'today' ? 'active' : ''}`}
            onClick={() => {
              setDateFilter('today');
              setCustomStartDate('');
              setCustomEndDate('');
            }}
          >
            📅 Днес
          </button>
          <button 
            className={`date-filter-button ${dateFilter === 'week' ? 'active' : ''}`}
            onClick={() => {
              setDateFilter('week');
              setCustomStartDate('');
              setCustomEndDate('');
            }}
          >
            📅 Последна Седмица
          </button>
          <button 
            className={`date-filter-button ${dateFilter === 'month' ? 'active' : ''}`}
            onClick={() => {
              setDateFilter('month');
              setCustomStartDate('');
              setCustomEndDate('');
            }}
          >
            📅 Последен Месец
          </button>
          <button 
            className={`date-filter-button ${dateFilter === 'all' ? 'active' : ''}`}
            onClick={() => {
              setDateFilter('all');
              setCustomStartDate('');
              setCustomEndDate('');
            }}
          >
            📋 Покажи всички
          </button>
        </div>

        <div className="additional-filters-container">
          <div className="filter-group">
            <label htmlFor="search-input">🔍 Търсене:</label>
            <input
              id="search-input"
              type="text"
              className="filter-input"
              placeholder="Търсене в името на пратката..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="start-date">📅 От дата (персонализирано):</label>
            <input
              id="start-date"
              type="date"
              className="filter-input"
              value={customStartDate}
              onChange={(e) => {
                setCustomStartDate(e.target.value);
                setDateFilter('custom');
              }}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="end-date">📅 До дата (персонализирано):</label>
            <input
              id="end-date"
              type="date"
              className="filter-input"
              value={customEndDate}
              onChange={(e) => {
                setCustomEndDate(e.target.value);
                setDateFilter('custom');
              }}
            />
          </div>
          {(searchText || customStartDate || customEndDate) && (
            <button 
              className="clear-additional-filters-button"
              onClick={() => {
                setSearchText('');
                setCustomStartDate('');
                setCustomEndDate('');
                setDateFilter('today');
              }}
              title="Изчисти допълнителните филтри"
            >
              ✕ Изчисти
            </button>
          )}
        </div>
      </>
      )}

      {(() => {
        const filteredShipments = getFilteredShipments();
        
        if (filteredShipments.length === 0) {
          return (
            <p>
              {searchText
                ? 'Няма пратки, отговарящи на търсенето.'
                : activeTab === 'completed' 
                  ? `Няма завършени пратки за избрания период.` 
                  : `Няма незавършени пратки.`}
            </p>
          );
        }
        
        return (
          <>
            {activeTab === 'completed' && searchText && (
              <div className="filter-results-count">
                Показани {filteredShipments.length} от {shipments.length} пратки
              </div>
            )}
            <div className="shipment-accordion">
              {filteredShipments.map((shipment) => (
            <div key={shipment.id} className="shipment-item">
              {processingShipment === shipment.id && (
                <div className="processing-overlay">
                  <div className="progress-bar">
                    <div className="progress-bar-fill"></div>
                  </div>
                  <span className="processing-text">
                    {shipment.status === 'completed' ? 'Отмяна на завършването...' : 'Завършване на пратката...'}
                  </span>
                </div>
              )}
              <div className="shipment-header" onClick={() => toggleShipment(shipment.id)}>
                <span>{shipment.name || `Пратка #${shipment.id}`}</span>
                <span>{new Date(shipment.created_at).toLocaleString('bg-BG')}</span>
                <span className={`status ${shipment.status}`}>{getStatusDisplayText(shipment.status)}</span>
                <div className="shipment-actions">
                  {canCompleteShipment(shipment) && (
                    <button 
                      className="complete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShipmentStatusChange(shipment.id);
                      }}
                      title="Завърши пратката"
                      disabled={processingShipment === shipment.id}
                    >
                      {processingShipment === shipment.id ? '⏳' : '✓'}
                    </button>
                  )}
                  {shipment.status === 'completed' && (
                    <button 
                      className="uncomplete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        uncompleteShipment(shipment.id);
                      }}
                      title="Отмени завършването на пратката"
                      disabled={processingShipment === shipment.id}
                    >
                      {processingShipment === shipment.id ? '⏳' : '🔄'}
                    </button>
                  )}

                  <button 
                    className="view-details-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      viewShipmentDetails(shipment);
                    }}
                    title="Преглед на детайли"
                  >
                    ⋯
                  </button>

                  <button 
                    className="export-button-small export-popup-container"
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = (e.target as HTMLElement).getBoundingClientRect();
                      setExportPopupPosition({
                        x: rect.right + 10,
                        y: rect.top
                      });
                      setExportPopupOpen(exportPopupOpen === shipment.id ? null : shipment.id);
                    }}
                    title="Експорт опции"
                    disabled={isExporting}
                  >
                    {isExporting ? '↓' : '⊞'}
                    <span style={{ marginLeft: '2px', fontSize: '10px' }}>▼</span>
                  </button>

                  {deleteEnabled && (
                    <button 
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteShipment(shipment.id);
                      }}
                      title="Изтрий пратка"
                    >
                      ×
                    </button>
                  )}
                  <span className="expand-icon">{expandedShipmentId === shipment.id ? '▲' : '▼'}</span>
                </div>
              </div>
              {expandedShipmentId === shipment.id && (
                <div className="shipment-details">
                  <h4>Артикули в пратката:</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Име на артикул</th>
                        <th>Изисквано количество</th>
                        <th>Действително количество</th>
                        <th>Статус</th>
                        <th>Проверен</th>
                        <th>Избрани партиди</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipment.items.map((item) => (
                        <React.Fragment key={item.id}>
                          <tr className={item.is_checked === false ? 'unchecked-item' : ''}>
                          <td>
                            {item.is_checked === false && <span className="unchecked-indicator">⚠️ </span>}
                            {item.name}
                          </td>
                          <td>{item.required_quantity}</td>
                          <td>
                            {editingItem === item.id ? (
                              <input
                                type="number"
                                value={editForm.actual_quantity}
                                onChange={(e) => setEditForm({...editForm, actual_quantity: e.target.value})}
                                className="edit-input"
                                placeholder="Количество"
                              />
                            ) : (
                              item.actual_quantity || 'N/A'
                            )}
                          </td>
                          <td>
                            {editingItem === item.id ? (
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={editForm.is_completed}
                                  onChange={(e) => setEditForm({...editForm, is_completed: e.target.checked})}
                                />
                                Завършен
                              </label>
                            ) : (
                              <span className={`status-badge ${item.is_completed ? 'completed' : 'pending'}`}>
                                {item.is_completed ? 'Завършен' : 'Незавършен'}
                              </span>
                            )}
                          </td>
                          <td>
                            {editingItem === item.id ? (
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={editForm.is_checked}
                                  onChange={(e) => setEditForm({...editForm, is_checked: e.target.checked})}
                                />
                                Проверен
                              </label>
                            ) : (
                              <span className={`status-badge ${item.is_checked !== false ? 'checked' : 'unchecked'}`}>
                                {item.is_checked !== false ? '✓' : '⚠️'}
                              </span>
                            )}
                          </td>
                          <td>
                            <ul>
                              {item.batches.map((batch) => (
                                <li key={batch.id}>
                                  <strong>{batch.zoho_batch_number}</strong> 
                                  {batch.quantity_selected > 0 && ` (Заявено: ${batch.quantity_selected} бр.)`}
                                  {batch.storage_locations && batch.storage_locations.length > 0 && (
                                    <div className="storage-locations">
                                      <small>Местоположения:</small>
                                      <ul className="storage-list">
                                        {batch.storage_locations.map((storage) => (
                                          <li key={storage.id} className="storage-item">
                                            📦 {storage.storage_name} - 🏢 {storage.warehouse_name} 
                                            ({storage.quantity || 0}
                                            {storage.total_available_quantity !== undefined && storage.total_available_quantity !== null 
                                              ? ` / ${storage.total_available_quantity}` 
                                              : ''} бр.)
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td>
                            {editingItem === item.id ? (
                              <div className="edit-actions">
                                <button 
                                  className="action-button save"
                                  onClick={() => saveEditItem(shipment.id, item.id)}
                                >
                                  💾 Запази
                                </button>
                                <button 
                                  className="action-button cancel"
                                  onClick={cancelEditItem}
                                >
                                  × Откажи
                                </button>
                              </div>
                            ) : (
                              <div className="item-actions">
                                <button 
                                  className="action-button notes"
                                  onClick={() => viewItemNotes(shipment.id, item.id, viewingNotesItem === item.id)}
                                  title={item.notes ? "Вижте бележки" : "Няма бележки"}
                                >
                                  📝
                                </button>
                                <button 
                                  className="action-button edit"
                                  onClick={() => startEditItem(item)}
                                >
                                  ✏️ Редактирай
                                </button>
                                <button 
                                  className="action-button remove"
                                  onClick={() => removeItemFromShipment(shipment.id, item.id)}
                                  disabled={processingShipment === shipment.id}
                                >
                                  × Премахни
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {viewingNotesItem === item.id && (
                          <tr className="notes-row">
                            <td colSpan={6} className="notes-cell">
                              <div className="notes-content">
                                <div className="notes-header">
                                  <strong>📝 Бележки:</strong>
                                </div>
                                <div className="notes-text">
                                  {item.notes || <em>Няма добавени бележки за този артикул.</em>}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {editingNotesItem === item.id && (
                          <tr className="notes-edit-row">
                            <td colSpan={6} className="notes-edit-cell">
                              <div className="notes-edit-content">
                                <strong>📝 Редактиране на бележки:</strong>
                                <textarea
                                  value={notesEditForm}
                                  onChange={(e) => setNotesEditForm(e.target.value)}
                                  className="notes-textarea"
                                  placeholder="Добавете бележки за този артикул..."
                                  rows={4}
                                />
                                <div className="notes-edit-actions">
                                  <button 
                                    className="action-button save"
                                    onClick={() => saveNotesEdit(shipment.id, item.id)}
                                  >
                                    💾 Запази
                                  </button>
                                  <button 
                                    className="action-button cancel"
                                    onClick={cancelEditingNotes}
                                  >
                                    × Откажи
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {editingItem === item.id && (
                          <tr className="edit-notes-row">
                            <td colSpan={6} className="edit-notes-cell">
                              <div className="edit-notes-content">
                                <label className="notes-label">
                                  <strong>📝 Бележки:</strong>
                                  <textarea
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                                    className="notes-textarea"
                                    placeholder="Добавете бележки за този артикул..."
                                    rows={3}
                                  />
                                </label>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
              ))}
            </div>
          </>
        );
      })()}

      {exportPopupOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            zIndex: 10000,
            pointerEvents: 'auto'
          }}
          onClick={() => setExportPopupOpen(null)}
        >
          <div 
            className="export-popup"
            style={{
              position: 'absolute',
              left: exportPopupPosition.x,
              top: exportPopupPosition.y,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              minWidth: '180px',
              overflow: 'hidden',
              animation: 'fadeIn 0.15s ease-out',
              zIndex: 10001,
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '8px 0' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Excel export clicked for shipment:', exportPopupOpen);
                  const shipmentId = exportPopupOpen;
                  if (!shipmentId) return;
                  
                  const shipmentName = shipments.find(s => s.id === shipmentId)?.name || `Пратка #${shipmentId}`;
                  
                  
                  setExportPopupOpen(null);
                  
                  
                  requestAnimationFrame(() => {
                    exportShipment(shipmentId, shipmentName);
                  });
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  pointerEvents: 'auto'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                ⊞ Excel експорт
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Zoho export clicked for shipment:', exportPopupOpen);
                  const shipmentId = exportPopupOpen;
                  if (!shipmentId) return;
                  
                  const shipmentName = shipments.find(s => s.id === shipmentId)?.name || `Пратка #${shipmentId}`;
                  
                  
                  setExportPopupOpen(null);
                  
                  
                  requestAnimationFrame(() => {
                    exportForZoho(shipmentId, shipmentName);
                  });
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  pointerEvents: 'auto'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
              >
                ⚙ Zoho експорт
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentList; 